'use client';

import { createContext, useContext, type ReactNode } from 'react';
import type {
  AvailabilitySlot,
  Bill,
  Booking,
  CreateBookingRequest,
  Menu,
  Orderability,
  RestaurantSite,
  SlotQuery,
} from './wire';
import type {
  AddLineInput,
  Cart,
  CheckoutInput,
  DinerFulfilmentType,
  PlacedOrder,
  SetFulfilmentInput,
} from './orderWire';

/**
 * The host-data-source contract the restaurant blocks read through.
 *
 * <p>Modelled on `CommerceSource` in `@akademi/site-builder-runtime` and mounted
 * the same way: one provider near the root, `null` when no host has mounted one,
 * and a consuming block renders a placeholder rather than throwing. **A block
 * never calls `fetch`.** That is what makes a menu block portable, and it is why
 * this contract exists before any of the blocks that read it.
 *
 * <h3>Why it lives in the app and not in the runtime package</h3>
 *
 * <p>`CommerceSource` sits in `@akademi/site-builder-runtime/src/portal/`,
 * alongside `BlogSource` and `LearningSource`, and that is where a
 * `HospitalitySource` eventually belongs too — the moment a second host wants
 * restaurant blocks. It is defined here for now because that package is shared
 * with Akademi and is another owner's to change; upstreaming it is a reported
 * follow-up, not something to fork. Nothing about the shape below would have to
 * change to move it.
 *
 * <h3>No method takes a tenant id, and only one takes a host</h3>
 *
 * <p>Every call resolves its tenant from the request host, server-side. The
 * browser-facing implementation goes through this app's own
 * `/api/storefront/hospitality/**` handlers, which read the host from the
 * request; the server-side reader takes a `host` argument because on the server
 * that value came out of the request's own headers rather than out of anything
 * the visitor chose. An anonymous visitor able to supply a tenant, a channel or
 * a domain is a tenant-isolation hole, not a convenience.
 *
 * <h3>Most of this surface is not anonymously reachable yet</h3>
 *
 * <p>Four anonymous resources exist today, all under `StorefrontWsPaths.ROOT`
 * and all resolving their channel from the hostname: `SiteWs`, `PublicMenuWs`,
 * `PublicAvailabilityWs` and `PublicBookingWs`. `BillWs` is not among them — it
 * is mapped under `CommerceWsPaths.ROOT`, which opens with `/{tenantId}`, and
 * requires `COMPANY_ADMIN` or `ORGANISATION_ADMIN`. There is no diner-facing
 * bill read.
 *
 * <p>{@link HospitalitySource.bill} is therefore declared here and implemented
 * as a throw naming the gap. It is NOT implemented by calling the
 * tenant-scoped path with a tenant id this app happens to know: that would put
 * a tenant id in a URL an anonymous visitor's browser composes, which is the
 * exact hole the storefront's whole tenancy model exists to close, and it would
 * fail on the permission check anyway. A named throw is a gap a build can see;
 * a call that 403s in production is one it cannot.
 */

/** Which hospitality resource has no anonymous read, and what would fix it. */
export class HospitalityGapError extends Error {
  constructor(
    /** `menu`, `orderable`, `slots`, `booking`, `bill`. */
    readonly resource: string,
    /** The public endpoint that would close it. */
    readonly needs: string,
  ) {
    super(
      `no anonymous read for hospitality "${resource}": the storefront resolves its tenant from ` +
        `the request host, and the only endpoint that exists is tenant-scoped behind an admin ` +
        `permission. Needs ${needs}.`,
    );
    this.name = 'HospitalityGapError';
  }
}

export interface HospitalitySource {
  /**
   * The restaurant behind this hostname: channel, currency, timezone, address,
   * hours, closures, turn time, deposit and order lead time.
   *
   * <p>The bootstrap call. `null` means no channel claims this host — a 404,
   * and never another tenant's site or a default one.
   */
  site(): Promise<RestaurantSite | null>;

  /**
   * The whole menu resolved for an instant. Omit `at` for now.
   *
   * <p>No channel id. `PublicMenuWs` resolves the channel from the hostname
   * through `SiteService`, so the one the browser would have supplied is the
   * one it would have been given back — and a channel belonging to another
   * tenant stops being expressible rather than merely being refused. The
   * language rides on the source's own locale, set once where the provider is
   * mounted, because a locale is not a tenant selector.
   */
  menu(at?: string): Promise<Menu | null>;

  /**
   * Whether one item may be ordered right now, and the reason when not.
   *
   * <p>`productId` is the only id this surface accepts; the backend scopes it
   * by the resolved site before it is used. Read uncached on purpose — a dish
   * the kitchen has just 86'd has to grey out.
   */
  orderable(productId: number, at?: string): Promise<Orderability | null>;

  /** Bookable start times for a party on a local date. Empty is an answer. */
  slots(query: SlotQuery): Promise<AvailabilitySlot[]>;

  /** Takes a booking. `source` is always `WEB` and is set server-side. */
  createBooking(request: CreateBookingRequest, idempotencyKey: string): Promise<Booking>;

  /**
   * A diner looking their own booking up. The last four digits of the contact
   * phone are the second factor, and a wrong phone must be indistinguishable
   * from an unknown number — a distinct answer confirms the booking exists.
   */
  bookingByNumber(bookingNumber: string, phoneLast4: string): Promise<Booking | null>;

  /** One order seen as the bills a table asked for. */
  bill(orderId: number): Promise<Bill | null>;

  // ---------------------------------------------------------------------
  // Ordering (11.10b). Optional, and that is the contract, not laziness.
  //
  // A restaurant site that only publishes a menu and takes bookings mounts a
  // source without these, and `RestaurantOrder` renders its "ordering is not
  // available here" state instead of throwing. Making them required would say
  // every host must be able to take money, which is the opposite of true — and
  // would break every stub source in this app's tests the moment a method is
  // added. A block therefore checks for the method, never assumes it.
  //
  // They are backed by the ANONYMOUS phase-6 sale API, not by anything in the
  // hospitality package: `CartResource` (`@Path("storefront/carts")`) and
  // `CheckoutResource` (`@Path("storefront/checkout")`) are both
  // `@RequirePermission({ANONYMOUS})` and both resolve tenant and channel from
  // the request host. A restaurant order is a `Cart` with `MODIFIER` lines on
  // it — the same mechanism the till uses — so there was nothing to build a
  // second time. The 11.9 gap note that said a diner had no cart was reading
  // the hospitality package alone.
  //
  // The cart token never crosses into the browser: it is a bearer credential,
  // it lives in an `HttpOnly` cookie the BFF sets, and every method below
  // means "the cart my cookie names".
  // ---------------------------------------------------------------------

  /** The current cart, or `null` when the visitor has not started one. */
  cart?(): Promise<Cart | null>;

  /** Starts a cart. Idempotent: an existing cookie wins over a new cart. */
  startCart?(fulfilmentType?: DinerFulfilmentType): Promise<Cart | null>;

  /** One item plus its chosen modifier slots, added as `ITEM` + `MODIFIER`
   *  lines in one round trip. */
  addLine?(input: AddLineInput): Promise<Cart | null>;

  /** A decimal quantity — `CartLine.quantity` is scale 3, not an integer. */
  setLineQuantity?(lineId: number, quantity: string): Promise<Cart | null>;

  removeLine?(lineId: number): Promise<Cart | null>;

  /** Sets `Cart.fulfilmentType` and, in the same backend transaction, either
   *  `collectionLocation` or `deliveryAddress` — switching away from
   *  `DELIVERY` clears the address rather than leaving a stale one behind. */
  setFulfilment?(input: SetFulfilmentInput): Promise<Cart | null>;

  /** Converts the cart. `idempotencyKey` is generated once per attempt and
   *  re-sent unchanged on a retry, so a retry is not a second order. */
  checkout?(input: CheckoutInput, idempotencyKey: string): Promise<PlacedOrder | null>;
}

const HospitalitySourceContext = createContext<HospitalitySource | null>(null);

export function HospitalitySourceProvider({
  value,
  children,
}: {
  value: HospitalitySource;
  children: ReactNode;
}) {
  return (
    <HospitalitySourceContext.Provider value={value}>{children}</HospitalitySourceContext.Provider>
  );
}

/** The host-provided source, or `null` when no provider is mounted. A block
 *  renders its placeholder on `null` and on a thrown method — "backend not
 *  wired yet" is a normal state on this site, not an error. */
export function useHospitalitySource(): HospitalitySource | null {
  return useContext(HospitalitySourceContext);
}
